import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/i18n/LanguageContext';
import { MarketingHeader } from '@/components/layout/MarketingHeader';
import { MarketingFooter } from '@/components/layout/MarketingFooter';
import { trackEvent } from '@/lib/analytics';
import { Input } from '@/components/ui/input';
import {
  ArrowRight, Download, CheckCircle2, BarChart3,
  FileText, Loader2, AlertCircle,
} from 'lucide-react';
import './WorldCupBrandReport.css';

var copyData = require('./reportCopy');

var API = process.env.REACT_APP_BACKEND_URL || '';

var CFG = {
  enabled: true,
  reportId: 'world_cup_brand_winners_losers_2026',
  pdfUrlEn: '/reports/YBV-Sports-World-Cup-Brand-Winners-and-Losers-2026.pdf',
  pdfUrlEs: '/reports/YBV-Sports-Ganadores-y-Perdedores-Mundial-Valor-de-Marca-2026.pdf',
  filenameEn: 'YBV-Sports-World-Cup-Brand-Winners-and-Losers-2026.pdf',
  filenameEs: 'YBV-Sports-Ganadores-y-Perdedores-Mundial-Valor-de-Marca-2026.pdf',
  privacyPolicyPath: '/privacy-policy',
  indexUrlEn: 'https://sports.yourbrandvalue.com/world-cup-brand-index-tool.html',
  indexUrlEs: 'https://sports.yourbrandvalue.com/world-cup-brand-index-tool.html?lang=es',
};

function getUtms() {
  try {
    var p = new URLSearchParams(window.location.search);
    return { s: p.get('utm_source') || '', m: p.get('utm_medium') || '', c: p.get('utm_campaign') || '', ct: p.get('utm_content') || '', t: p.get('utm_term') || '' };
  } catch (e) { return { s: '', m: '', c: '', ct: '', t: '' }; }
}

function devType() {
  return typeof window !== 'undefined' ? (window.innerWidth < 768 ? 'mobile' : window.innerWidth < 1024 ? 'tablet' : 'desktop') : 'unknown';
}

function getPdf(lang) { return lang === 'es' ? CFG.pdfUrlEs : CFG.pdfUrlEn; }

function dlFile(url, lang) {
  if (!url) return;
  var a = document.createElement('a');
  a.href = url;
  a.download = lang === 'es' ? CFG.filenameEs : CFG.filenameEn;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

/* ── Lead Form ── */
function ReportForm(props) {
  var lang = props.lang;
  var c = props.c;
  var onOk = props.onOk;

  var sE = useState(''); var email = sE[0]; var setEmail = sE[1];
  var sN = useState(''); var name = sN[0]; var setName = sN[1];
  var sL = useState(false); var loading = sL[0]; var setLoading = sL[1];
  var sErr = useState(''); var error = sErr[0]; var setError = sErr[1];
  var eRef = useRef(null);
  var started = useRef(false);

  var trackStart = function() {
    if (!started.current) {
      started.current = true;
      trackEvent('world_cup_report_form_started', { language: lang, report_id: CFG.reportId, page_path: window.location.pathname, device_type: devType() });
    }
  };

  var submit = function(e) {
    e.preventDefault();
    if (loading) return;
    setError('');
    var t = email.trim().toLowerCase();
    if (!t || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(t)) {
      setError(c.errorEmail);
      if (eRef.current) eRef.current.focus();
      return;
    }
    setLoading(true);
    var u = getUtms();
    trackEvent('world_cup_report_form_submitted', { language: lang, report_id: CFG.reportId, page_path: window.location.pathname, device_type: devType() });
    fetch(API + '/api/report-leads', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: t, name: name.trim(), org_type: '', language: lang, report_id: CFG.reportId, page_url: window.location.href, utm_source: u.s, utm_medium: u.m, utm_campaign: u.c, utm_content: u.ct, referrer: document.referrer || '' }),
    }).then(function(r) {
      if (r.status === 422) throw new Error('invalid_email');
      if (r.status === 429) throw new Error('rate_limited');
      if (!r.ok) throw new Error('err');
      return r.json();
    }).then(function() { setLoading(false); onOk(); }).catch(function(err) {
      setLoading(false);
      if (err.message === 'invalid_email') setError(c.errorEmail);
      else if (err.message === 'rate_limited') setError(c.errorRate);
      else setError(c.errorGeneric);
      trackEvent('world_cup_report_error', { language: lang, error_type: err.message, device_type: devType() });
      if (eRef.current) eRef.current.focus();
    });
  };

  return (
    <form onSubmit={submit} className="wcr-form" data-testid="report-form-hero" id="report-form">
      <h3 className="wcr-form-title">{c.formTitle}</h3>
      <div className="wcr-form-fields">
        <div className="wcr-field">
          <label htmlFor="wcr-n" className="wcr-label">{c.nameLabel}</label>
          <Input id="wcr-n" value={name} onChange={function(ev){setName(ev.target.value);}} onFocus={trackStart}
            placeholder={c.namePlaceholder} className="wcr-input" data-testid="report-name-hero" autoComplete="name" />
        </div>
        <div className="wcr-field">
          <label htmlFor="wcr-e" className="wcr-label">{c.emailLabel} *</label>
          <Input ref={eRef} id="wcr-e" type="email" value={email} onChange={function(ev){setEmail(ev.target.value);}} onFocus={trackStart}
            placeholder={c.emailPlaceholder} required className="wcr-input" data-testid="report-email-hero"
            aria-describedby={error ? 'wcr-err' : undefined} autoComplete="email" />
        </div>
      </div>
      {error && <div className="wcr-error" id="wcr-err" role="alert" data-testid="report-error-hero"><AlertCircle size={14} /><span>{error}</span></div>}
      <button type="submit" disabled={loading} className="wcr-cta" data-testid="report-cta-hero">
        {loading ? <span className="wcr-cta-inner"><Loader2 size={18} className="wcr-spinner" />{c.loading}</span> : <span className="wcr-cta-inner">{c.cta}<ArrowRight size={18} /></span>}
      </button>
      <p className="wcr-trust">{c.trustLine} <Link to={CFG.privacyPolicyPath} className="wcr-trust-link">{c.privacyLink}</Link></p>
    </form>
  );
}

/* ── Success State ── */
function SuccessState(props) {
  var lang = props.lang;
  var c = props.c;
  var ref = useRef(null);
  useEffect(function() {
    if (ref.current) ref.current.focus();
    var url = getPdf(lang);
    if (url) {
      trackEvent('world_cup_report_download_started', { language: lang, report_id: CFG.reportId });
      setTimeout(function() { dlFile(url, lang); }, 600);
    }
  }, [lang]);
  var url = getPdf(lang);
  return (
    <div className="wcr-success" data-testid="report-success">
      <div className="wcr-success-icon"><CheckCircle2 size={28} /></div>
      <h3 ref={ref} tabIndex={-1} className="wcr-success-title">{c.successTitle}</h3>
      <p className="wcr-success-msg">{url ? c.successMsg : c.errorUnavailable}</p>
      {url && <a href={url} download className="wcr-cta wcr-cta-dl" data-testid="report-download-btn"
        onClick={function(){trackEvent('world_cup_report_manual_download',{language:lang});}}><Download size={18} />{c.downloadCta}</a>}
    </div>
  );
}

/* ── Report Cover Visual ── */
function ReportCover() {
  return (
    <div className="wcr-cover" aria-hidden="true">
      <div className="wcr-cover-main">
        <FileText size={22} className="wcr-cover-icon" />
        <div className="wcr-cover-badge">WORLD CUP BRAND INDEX</div>
        <div className="wcr-cover-title">Winners &amp; Losers<br />in Brand Value</div>
        <div className="wcr-cover-bars"><div className="wcr-bar" style={{width:'88%'}} /><div className="wcr-bar" style={{width:'74%'}} /><div className="wcr-bar" style={{width:'66%'}} /><div className="wcr-bar" style={{width:'52%'}} /></div>
        <div className="wcr-cover-brand">Your Brand Value Sports</div>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════
   MAIN PAGE
   ════════════════════════════════════════ */
export default function WorldCupBrandReport() {
  var langCtx = useLanguage();
  var lang = langCtx.lang === 'es' ? 'es' : 'en';
  var c = lang === 'es' ? copyData.ES : copyData.EN;

  var sDone = useState(false); var done = sDone[0]; var setDone = sDone[1];

  useEffect(function() {
    trackEvent('world_cup_report_page_view', { language: lang, report_id: CFG.reportId, page_path: window.location.pathname, device_type: devType() });
  }, [lang]);

  var onOk = function() { setDone(true); };

  var scrollToForm = function(e) {
    e.preventDefault();
    trackEvent('world_cup_report_final_cta_click', { language: lang, device_type: devType() });
    var el = document.getElementById('report-form');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  return (
    <div className="wcr-page">
      <MarketingHeader />

      {/* ═══ 1. HERO + FORM ═══ */}
      <section className="wcr-hero" data-testid="report-hero">
        <div className="wcr-hero-bg" aria-hidden="true"><div className="wcr-glow-1" /><div className="wcr-glow-2" /></div>
        <div className="wcr-container wcr-hero-grid">
          <div className="wcr-hero-left">
            <div className="wcr-eyebrow" data-testid="report-eyebrow">{c.eyebrow}</div>
            <h1 className="wcr-h1" data-testid="report-headline">{c.headline}</h1>
            <p className="wcr-subtitle">{c.subtitle}</p>
            <ul className="wcr-proof">
              <li><span className="wcr-dot" />{c.cred1}</li>
              <li><span className="wcr-dot" />{c.cred2}</li>
              <li><span className="wcr-dot" />{c.cred3}</li>
            </ul>
          </div>
          <div className="wcr-hero-right">
            {done ? <SuccessState lang={lang} c={c} /> : <ReportForm lang={lang} c={c} onOk={onOk} />}
          </div>
        </div>
      </section>

      {/* ═══ 2. WHAT THE REPORT INCLUDES ═══ */}
      <section className="wcr-section" data-testid="report-includes">
        <div className="wcr-container wcr-narrow">
          <h2 className="wcr-h2">{c.includesTitle}</h2>
          <ul className="wcr-bullets">
            <li><span className="wcr-bullet-dash" />{c.includesBullets[0]}</li>
            <li><span className="wcr-bullet-dash" />{c.includesBullets[1]}</li>
            <li><span className="wcr-bullet-dash" />{c.includesBullets[2]}</li>
            <li><span className="wcr-bullet-dash" />{c.includesBullets[3]}</li>
          </ul>
        </div>
      </section>

      {/* ═══ 3. WHY IT MATTERS ═══ */}
      <section className="wcr-section wcr-section--alt" data-testid="report-why">
        <div className="wcr-container">
          <h2 className="wcr-h2">{c.whyTitle}</h2>
          <div className="wcr-audience-grid">
            <div className="wcr-audience-card"><h3 className="wcr-audience-title">{c.whyBlocks[0].audience}</h3><p className="wcr-audience-text">{c.whyBlocks[0].text}</p></div>
            <div className="wcr-audience-card"><h3 className="wcr-audience-title">{c.whyBlocks[1].audience}</h3><p className="wcr-audience-text">{c.whyBlocks[1].text}</p></div>
            <div className="wcr-audience-card"><h3 className="wcr-audience-title">{c.whyBlocks[2].audience}</h3><p className="wcr-audience-text">{c.whyBlocks[2].text}</p></div>
            <div className="wcr-audience-card"><h3 className="wcr-audience-title">{c.whyBlocks[3].audience}</h3><p className="wcr-audience-text">{c.whyBlocks[3].text}</p></div>
          </div>
        </div>
      </section>

      {/* ═══ 4. COMPACT METHODOLOGY ═══ */}
      <section className="wcr-section" data-testid="report-methodology">
        <div className="wcr-container wcr-narrow wcr-meth">
          <BarChart3 size={18} className="wcr-meth-icon" />
          <h2 className="wcr-h2">{c.methTitle}</h2>
          <p className="wcr-meth-desc">{c.methDesc}</p>
          <div className="wcr-badges">
            <span className="wcr-badge">{c.methBadges[0]}</span>
            <span className="wcr-badge">{c.methBadges[1]}</span>
            <span className="wcr-badge">{c.methBadges[2]}</span>
            <span className="wcr-badge">{c.methBadges[3]}</span>
          </div>
          <p className="wcr-disclaimer">{c.methNote}</p>
        </div>
      </section>

      {/* ═══ 5. FINAL CTA ═══ */}
      <section className="wcr-final" data-testid="report-final-cta">
        <div className="wcr-container wcr-final-inner">
          <h2 className="wcr-final-title">{c.finalTitle}</h2>
          <p className="wcr-final-desc">{c.finalDesc}</p>
          {done
            ? <SuccessState lang={lang} c={c} />
            : <button className="wcr-cta wcr-cta-final" onClick={scrollToForm} data-testid="report-final-cta-btn"><span className="wcr-cta-inner">{c.finalCta}<ArrowRight size={18} /></span></button>
          }
        </div>
      </section>

      {/* ═══ 6. GLOBAL FOOTER ═══ */}
      <MarketingFooter />
    </div>
  );
}
